import { useMemo, useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { FileDeletionType } from '../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDeletedFiles } from '@/api/queries/useGetDeletedFiles';
import formatDate from '@/helpers/formatDate';
import { downloadDeletedFilesReport } from '@/helpers/report-utils';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

interface FlattenedDeletion {
  id: string;
  filename: string;
  deletedAt: string;
  deletedBy: string;
  type: FileDeletionType;
}

const columnHelper = createColumnHelper<FlattenedDeletion>();

interface DeletedFilesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
}

export function DeletedFilesModal({ open, onOpenChange, matterId }: DeletedFilesModalProps) {
  const [globalFilter, setGlobalFilter] = useState('');
  const { data: deletionHistory, isLoading } = useGetDeletedFiles(matterId);

  // Flatten the deletion history into individual file rows
  const flattenedDeletions = useMemo(() => {
    if (!deletionHistory) return [];

    const result: FlattenedDeletion[] = [];
    for (const history of deletionHistory) {
      for (const file of history.fileDeletion) {
        result.push({
          id: `${history.id}-${file.filename}`,
          filename: file.filename,
          deletedAt: history.createdAt,
          deletedBy: history.createdBy.email,
          type: history.type,
        });
      }
    }
    return result;
  }, [deletionHistory]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('filename', {
        header: () => <span>Filename</span>,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <Icon className="lucide--file h-3.5 w-3.5 text-gray-400" />
            <span className="text-sm text-gray-800">{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('type', {
        header: () => <span>Deletion Type</span>,
        cell: (info) => {
          const type = info.getValue();
          const isStaged = type === FileDeletionType.Staged;
          return (
            <Badge variant={isStaged ? 'secondary' : 'default'} className="text-xs">
              <Icon className={`${isStaged ? 'lucide--clock' : 'lucide--send'} mr-1 h-3 w-3`} />
              {isStaged ? 'Before Submission' : 'After Submission'}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('deletedBy', {
        header: () => <span>Deleted By</span>,
        cell: (info) => <span className="text-sm text-gray-600">{info.getValue()}</span>,
      }),
      columnHelper.accessor('deletedAt', {
        header: () => <span>Date</span>,
        cell: (info) => <span className="text-sm text-gray-600">{formatDate(info.getValue(), true, true)}</span>,
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: flattenedDeletions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-6xl flex-col">
        <DialogHeader>
          <DialogTitle>Deleted Files</DialogTitle>
          <DialogDescription>Files that have been permanently removed from this matter</DialogDescription>
        </DialogHeader>

        <div className="mb-3">
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              trackCustomEvent(TrackingEventsV2.FileHistoryDownloadDeletedReport, { matterId });
              deletionHistory && downloadDeletedFilesReport(deletionHistory, matterId);
            }}
            disabled={!deletionHistory || flattenedDeletions.length === 0}
          >
            <Icon className="lucide--download h-3.5 w-3.5" />
            Download Report
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          <ScrollableTable
            table={table}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            plural="deleted files"
            loading={isLoading}
            className="h-full max-h-[50vh] p-0"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
